import { fmtDate, fmtMonth, todayISO, weekdayOf } from './format.js';
import { WEEKDAYS } from './model.js';
import { computeStats } from './stats.js';
import { countDays, selectedDays } from './timefilter.js';

/* --------------------------------------------------------------------------
   Tidsupplösning

   Serier kan grupperas per dag, vecka eller månad. Veckor följer ISO 8601,
   alltså måndag som första dag och vecka 1 är den vecka som innehåller
   4 januari — samma veckonummer som svenska kalendrar visar.
   -------------------------------------------------------------------------- */

export const MONTHS_SHORT = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];

/** ISO-vecka för ett datum. Returnerar {year, week}. */
export function isoWeek(iso){
  const [y,m,d] = iso.slice(0,10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m-1, d));
  // Veckans år bestäms av torsdagen i samma vecka
  const dayNum = (dt.getUTCDay() + 6) % 7;      // måndag = 0
  dt.setUTCDate(dt.getUTCDate() - dayNum + 3);
  const isoYear = dt.getUTCFullYear();
  const firstThu = new Date(Date.UTC(isoYear, 0, 4));
  const firstDayNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstDayNum + 3);
  return {year: isoYear, week: 1 + Math.round((dt - firstThu) / (7 * 86400000))};
}

/** Sorterbar nyckel för en tidsperiod. */
export function bucketKey(day, grain){
  if(grain === 'month') return day.slice(0,7);
  if(grain === 'week'){
    const {year, week} = isoWeek(day);
    return `${year}-v${String(week).padStart(2,'0')}`;
  }
  return day;
}

/** Läsbar etikett för en tidsperiod. */
export function bucketLabel(key, grain){
  if(grain === 'month'){
    const [y,m] = key.split('-').map(Number);
    return `${MONTHS_SHORT[m-1]} ${y}`;
  }
  if(grain === 'week'){
    const [y,w] = key.split('-v');
    return `v${Number(w)} ${y}`;
  }
  const [,m,d] = key.split('-').map(Number);
  return `${d} ${MONTHS_SHORT[m-1]}`;
}

/** Första och sista kalenderdag som en tidsperiod omfattar. */
export function bucketRange(key, grain){
  if(grain === 'month'){
    return {from: `${key}-01`, to: `${key}-${String(daysInMonth(key)).padStart(2,'0')}`};
  }
  if(grain === 'week'){
    const [y,w] = key.split('-v').map(Number);
    // Måndagen i ISO-vecka w: utgå från 4 januari och stega till rätt vecka
    const jan4 = new Date(Date.UTC(y, 0, 4));
    const jan4Day = (jan4.getUTCDay() + 6) % 7;
    const monday = new Date(Date.UTC(y, 0, 4 - jan4Day + (w - 1) * 7));
    const sunday = new Date(monday.getTime() + 6 * 86400000);
    return {from: monday.toISOString().slice(0,10), to: sunday.toISOString().slice(0,10)};
  }
  return {from: key, to: key};
}

/**
 * Delar upp kvitton i tidsperioder och räknar fram full statistik för varje.
 * Varje kvitto behandlas en gång, så kostnaden blir densamma som en vanlig
 * genomräkning oavsett vald upplösning.
 *
 * bounds är första och sista dag som serien täcker, {min, max} — filens,
 * eller den valda periodens när utvecklingsvyn visar den. De avgör om
 * ytterperioderna är kompletta, oavsett vilket urval receipts är.
 *
 * days är de valda veckodagarna (Set) eller null. Med bara fredagar valda är
 * en vecka komplett när dess fredag ligger inom bounds; räknat på alla sju
 * dagar skulle varje vecka se ofullständig ut.
 */
export function buildSeries(receipts, grain, bounds, days = null){
  const groups = new Map();
  for(const r of receipts){
    const k = bucketKey(r.day, grain);
    let g = groups.get(k);
    if(!g){ g = []; groups.set(k, g); }
    g.push(r);
  }
  // Filens första och sista dag avgör om ytterperioderna är kompletta.
  // En månad som bara har tolv dagar data ska inte jämföras rakt av mot en hel.
  const fileFrom = bounds ? bounds.min : null;
  const fileTo   = bounds ? bounds.max : null;

  return [...groups.entries()]
    .sort((a,b) => a[0] < b[0] ? -1 : 1)
    .map(([key, list]) => {
      let partial = false;
      if(grain !== 'day' && fileFrom !== null){
        const r = bucketRange(key, grain);
        const from = r.from > fileFrom ? r.from : fileFrom;
        const to   = r.to   < fileTo   ? r.to   : fileTo;
        const covered = countDays(from, to, days);
        const total   = countDays(r.from, r.to, days);
        // En enstaka dag hit eller dit gör perioden fullt jämförbar. Först när
        // en tiondel saknas blir jämförelser mot hela perioder missvisande.
        partial = total > 0 && covered / total < 0.9;
      }
      return {key, label: bucketLabel(key, grain), partial, stats: computeStats(list)};
    });
}

/* --------------------------------------------------------------------------
   Jämförelseperiod

   Den valda perioden jämförs med den närmast föregående lika långa perioden:
   en dag mot dagen innan, en månad mot månaden innan, ett intervall mot ett
   lika långt intervall precis före. Så blir jämförelsen alltid rättvis i längd.
   -------------------------------------------------------------------------- */

/** Antal dagar mellan två datumsträngar, inklusive båda ändpunkterna. */
export function daysBetween(a, b){
  const [y1,m1,d1] = a.split('-').map(Number);
  const [y2,m2,d2] = b.split('-').map(Number);
  const t1 = Date.UTC(y1, m1-1, d1), t2 = Date.UTC(y2, m2-1, d2);
  return Math.round((t2 - t1) / 86400000) + 1;
}

/** Flyttar ett datum n dagar. Returnerar "YYYY-MM-DD". */
export function shiftDay(iso, n){
  const [y,m,d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m-1, d + n));
  return t.toISOString().slice(0,10);
}

/** Föregående månad som "YYYY-MM". */
export function prevMonth(ym){
  let [y,m] = ym.split('-').map(Number);
  m -= 1;
  if(m === 0){ m = 12; y -= 1; }
  return `${y}-${String(m).padStart(2,'0')}`;
}

/** Antal dagar i en månad, "YYYY-MM". */
export function daysInMonth(ym){
  const [y,m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Flyttar ett datum ett helt år bakåt. Hanterar 29 februari. */
export function shiftYear(iso, n){
  const [y,m,d] = iso.split('-').map(Number);
  const maxDay = new Date(Date.UTC(y + n, m, 0)).getUTCDate();
  return `${y + n}-${String(m).padStart(2,'0')}-${String(Math.min(d, maxDay)).padStart(2,'0')}`;
}

/** Sista dagen i månaden som filen har data för. all är hela filen,
    inte urvalet: täckningen är filens, inte butikens eller gruppens. */
function lastDayWithData(all, month){
  return all
    .filter(r => r.month === month)
    .reduce((mx, r) => Math.max(mx, Number(r.day.slice(8,10))), 0);
}

/* Med veckodagar valda jämförs samma veckodag. En dag jämförs då med samma
   veckodag veckan innan — dagen innan ingår kanske inte ens i urvalet, och
   med fredag och lördag valda vore en lördag mot en fredag ingen rättvis
   jämförelse. Ett intervall flyttas hela veckor bakåt, så att
   jämförelseperioden har exakt samma veckodagar. Samma period förra året
   flyttas 52 veckor (364 dagar), så att en fredag jämförs med en fredag. */
const WEEKS_PER_YEAR_DAYS = 364;

/**
 * Månadsjämförelse. Är den valda månaden ofullständig jämförs lika många
 * dagar in i jämförelsemånaden: tolv dagar mot en hel månad ger ett kraftigt
 * negativt utfall som inte betyder något. Med veckodagar valda räknas också
 * hur många av dem varje månad har — fem fredagar mot fyra är 25 % mer
 * omsättning utan att något har hänt, och det ska kunden få veta.
 */
function monthComparison(f, scoped, all, p, label, days){
  // Dagarna räknas bara där filen har data: börjar filen mitt i
  // jämförelsemånaden har den inte alla den månadens fredagar
  const fileMin = all.length ? all[0].day : '', fileMax = all.length ? all[all.length - 1].day : '';
  const inFile = (a, b) => countDays(a < fileMin ? fileMin : a, b > fileMax ? fileMax : b, days);
  const daysWithData = lastDayWithData(all, f.month);
  const partial = daysWithData > 0 && daysWithData < daysInMonth(f.month);
  const cutoff  = partial ? daysWithData : 31;
  const out = {
    list: scoped.filter(r => r.month === p && Number(r.day.slice(8,10)) <= cutoff),
    label: partial ? `1–${cutoff} ${label}` : label,
    partial
  };
  if(days){
    const end = (ym, c) => `${ym}-${String(Math.min(c, daysInMonth(ym))).padStart(2,'0')}`;
    out.dayCounts = {now: inFile(f.month + '-01', end(f.month, cutoff)),
                     before: inFile(p + '-01', end(p, cutoff))};
  }
  return out;
}

/**
 * Samma period ett år tidigare. Används när kunden vill se säsongsutveckling
 * i stället för utvecklingen mot närmast föregående period.
 * all är filens alla kvitton, scoped urvalet som ska jämföras.
 */
export function yearAgoPeriod(f, scoped, all){
  const days = selectedDays(f.weekdays);
  const back = iso => days ? shiftDay(iso, -WEEKS_PER_YEAR_DAYS) : shiftYear(iso, -1);
  const basis = days ? 'weekday' : 'calendar';

  if(f.mode === 'today' || f.mode === 'date'){
    const base = f.mode === 'today' ? todayISO() : f.date;
    if(!base) return null;
    const y = back(base);
    return {list: scoped.filter(r => r.day === y), label: fmtDate(y), basis};
  }
  if(f.mode === 'month' && f.month){
    const [yy,mm] = f.month.split('-').map(Number);
    const p = `${yy-1}-${String(mm).padStart(2,'0')}`;
    return {...monthComparison(f, scoped, all, p, fmtMonth(p), days), basis: 'calendar'};
  }
  if(f.mode === 'range'){
    let a = f.from, b = f.to;
    if(!a || !b) return null;
    if(a > b) [a,b] = [b,a];
    const pa = back(a), pb = back(b);
    return {list: scoped.filter(r => r.day >= pa && r.day <= pb),
            label: `${fmtDate(pa)} – ${fmtDate(pb)}`, basis};
  }
  return null;
}

/**
 * Returnerar kvitton för perioden före den valda, samt en etikett.
 * Ger null när jämförelse inte är meningsfull.
 *
 * basis anger hur perioden valdes: 'calendar' närmast föregående lika långa
 * period, 'weekday' samma veckodagar ett antal hela veckor tidigare.
 */
export function previousPeriod(f, scoped, all){
  const days = selectedDays(f.weekdays);

  if(f.mode === 'today' || (f.mode === 'date' && f.date)){
    const base = f.mode === 'today' ? todayISO() : f.date;
    if(days){
      const y = shiftDay(base, -7);
      return {list: scoped.filter(r => r.day === y),
              label: `förra ${WEEKDAYS[weekdayOf(y)].toLowerCase()}en`, basis:'weekday'};
    }
    const y = shiftDay(base, -1);
    return {list: scoped.filter(r => r.day === y),
            label: f.mode === 'today' ? 'igår' : 'dagen innan', basis:'calendar'};
  }
  if(f.mode === 'month' && f.month){
    const p = prevMonth(f.month);
    return {...monthComparison(f, scoped, all, p, fmtMonth(p).replace(/ \d{4}$/,''), days), basis:'calendar'};
  }
  if(f.mode === 'range'){
    let a = f.from, b = f.to;
    if(!a || !b) return null;
    if(a > b) [a,b] = [b,a];
    const len = daysBetween(a, b);
    if(days){
      const weeks = Math.ceil(len / 7);
      const pa = shiftDay(a, -7 * weeks), pb = shiftDay(b, -7 * weeks);
      return {list: scoped.filter(r => r.day >= pa && r.day <= pb),
              label: `samma veckodagar ${weeks === 1 ? 'veckan' : weeks + ' veckor'} innan`,
              basis:'weekday'};
    }
    const pb  = shiftDay(a, -1);
    const pa  = shiftDay(pb, -(len - 1));
    return {list: scoped.filter(r => r.day >= pa && r.day <= pb),
            label: `föregående ${len} ${len === 1 ? 'dag' : 'dagar'}`, basis:'calendar'};
  }
  return null;
}

/* --------------------------------------------------------------------------
   Den valda periodens gränser och snabbval för intervall
   -------------------------------------------------------------------------- */

/**
 * Första och sista dag i den valda perioden, begränsat till filen. Används
 * när utvecklingsvyn visar bara den valda perioden. Ger null när perioden
 * ligger helt utanför filen.
 */
export function periodBounds(f, bounds){
  let a = bounds.min, b = bounds.max;
  if(f.mode === 'today'){ a = b = todayISO(); }
  else if(f.mode === 'date' && f.date){ a = b = f.date; }
  else if(f.mode === 'month' && f.month){
    a = f.month + '-01';
    b = `${f.month}-${String(daysInMonth(f.month)).padStart(2,'0')}`;
  }else if(f.mode === 'range'){
    a = f.from || bounds.min; b = f.to || bounds.max;
    if(a > b) [a,b] = [b,a];
  }
  if(a < bounds.min) a = bounds.min;
  if(b > bounds.max) b = bounds.max;
  return a <= b ? {min:a, max:b} : null;
}

/**
 * Snabbval för intervallet. Räknas från filens sista dag, inte från dagens
 * datum: exporten är historik, och "senaste fyra veckorna" i en fil som slutar
 * i augusti betyder fyra veckor fram till augusti.
 *
 * Hela veckor slutar på söndag. Slutar filen mitt i en vecka räknas veckorna
 * fram till söndagen innan, så att varje vecka i utvecklingsvyn är komplett.
 *
 * Ett snabbval som filen inte räcker till ger null och visas avstängt, i
 * stället för att i tysthet klippas: "senaste 4 hela veckorna" i en fil på
 * tre veckor vore varken fyra eller hela. Bara "hittills i år" klipps vid
 * filens första dag, eftersom det är allt filen har av året.
 */
export const RANGE_PRESETS = [
  {key:'file',    label:'Hela filen'},
  {key:'last7',   label:'Senaste 7 dagarna'},
  {key:'last30',  label:'Senaste 30 dagarna'},
  {key:'weeks4',  label:'Senaste 4 hela veckorna'},
  {key:'weeks12', label:'Senaste 12 hela veckorna'},
  {key:'ytd',     label:'Hittills i år'}
];

/** {from, to} för ett snabbval, eller null när filen inte räcker till det. */
export function rangePreset(key, bounds){
  const {min, max} = bounds;
  const within = (from, to) => from >= min ? {from, to} : null;
  if(key === 'file')   return {from:min, to:max};
  if(key === 'last7')  return within(shiftDay(max, -6), max);
  if(key === 'last30') return within(shiftDay(max, -29), max);
  if(key === 'weeks4' || key === 'weeks12'){
    const wd  = weekdayOf(max);
    const end = wd === 6 ? max : shiftDay(max, -(wd + 1));
    return within(shiftDay(end, -(7 * (key === 'weeks4' ? 4 : 12) - 1)), end);
  }
  if(key === 'ytd'){
    const jan1 = max.slice(0,4) + '-01-01';
    return {from: jan1 < min ? min : jan1, to: max};
  }
  return null;
}

/** Procentuell förändring. Returnerar null när jämförelse saknar mening. */
export function change(now, before){
  if(!isFinite(now) || !isFinite(before) || before === 0) return null;
  return (now - before) / Math.abs(before) * 100;
}
